import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { classifyDeal } from "../data/dealHealth";
import { Link } from "react-router-dom";
import {
  Plus, BedDouble, Bath, ArrowRight, Home, CheckCircle2,
  ShieldAlert, AlertOctagon, ChevronDown, Filter, Upload, MoreHorizontal,
  FileText, Check, Archive, RotateCcw, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import {
  openAddProperty, openEditProperty, openCreateDeal, useChainStore,
} from "../data/chainStore";
import { openProperty } from "../components/PropertyDetailsModal";
import { PremiumInput } from "../components/brand/PremiumInput";
import { isActiveDeal } from "../data/dealFilters";

/* iter-249 — Properties pilot refinement.
 *
 * Calibrates the Properties page into the approved pilot-ready
 * operational inventory experience:
 *
 *   • KPI strip — 4 cards (Total / Under offer / Sales agreed /
 *     Needs attention). No "View all" links — this reads as an
 *     operational snapshot, not a reporting dashboard.
 *   • Filter row — search + 3 dropdowns (statuses / types /
 *     offices) + Filters. Dropdowns are visual scaffolds for the
 *     pilot; the search input keeps live filtering.
 *   • Property cards — refined to a 5-up grid with: status pill
 *     + assignee chip on the image, kebab top-right, address +
 *     postcode + beds/baths/type, price, concise operational
 *     progression chip ("Awaiting searches" / "Mortgage approved"
 *     / "Contracts pending" / "Searches overdue" / "Marketing
 *     prep" / "Coming soon" / "Exchange booked") with `N / 6
 *     complete` indicator, calm metadata row (Updated · Exch ·
 *     operational state), and one canonical CTA per card
 *     ("Open Deal Room" for active transactions, "Create Deal
 *     Room" for early-stage stock).
 *   • Bottom — calm "You've seen all N properties" confirmation
 *     replacing the legacy Load more affordance.
 *
 * Existing wiring preserved:
 *   • Search input still filters via `useChainStore`/local state.
 *   • CTAs reuse `openCreateDeal` (existing modal) and route to
 *     `/deal-rooms/<dealId>` via the canonical Link surface.
 *   • Card body still opens the existing PropertyDetailsModal via
 *     `openProperty(...)`.
 *
 * Data calibration lives at the PAGE level (same pattern as iter-
 * 248/249 Forecasting Centre calibration) — sampleData.js stays
 * untouched so Deal Rooms and other surfaces continue to read
 * the underlying seed unchanged.
 */

export const STATUS_TONES = {
  "FOR SALE":     { label: "For sale",    pill: "bg-emerald-50 text-emerald-700" },
  "UNDER OFFER":  { label: "Under offer", pill: "bg-amber-50 text-amber-700" },
  "SALES AGREED": { label: "Sold STC",    pill: "bg-violet-50 text-violet-700" },
  "EXCHANGED":    { label: "Exchanged",   pill: "bg-sky-50 text-sky-700" },
  "COMPLETED":    { label: "Completed",   pill: "bg-slate-100 text-slate-600" },
  "WITHDRAWN":    { label: "Withdrawn",   pill: "bg-slate-100 text-slate-600" },
  "DRAFT":        { label: "Draft",       pill: "bg-slate-100 text-slate-600" },
  "DRAFT LIVE":   { label: "Draft",       pill: "bg-slate-100 text-slate-600" },
  "COMING SOON":  { label: "Coming soon", pill: "bg-sky-50 text-sky-700" },
  "AT RISK":      { label: "At risk",     pill: "bg-rose-50 text-rose-600" },
};

/* iter-270B — Card-shape derivation for user-created properties.
 *
 * Operator-added properties from the Add Property modal land in
 * chainStore.properties with the API/camelCase shape. We project them
 * onto the same card vocabulary used by the pilot grid so newly-added
 * properties render with the same affordances (status pill, progression
 * chip, "Open Deal Room" CTA when a deal exists for the property). */
const STATUS_FROM_PROPERTY = {
  "Listed": "FOR SALE",
  "Under Offer": "UNDER OFFER",
  "Sales Agreed": "SALES AGREED",
  "Exchanged": "EXCHANGED",
  "Completed": "COMPLETED",
  "Withdrawn": "WITHDRAWN",
  "Draft": "DRAFT",
};

const _district = (postcode) => {
  const pc = String(postcode || "").trim().toUpperCase();
  if (!pc) return "";
  const parts = pc.split(/\s+/);
  if (parts.length > 1) return parts[0];
  return pc.length > 4 ? pc.slice(0, -3) : pc;
};

const _askingPrice = (raw) => {
  const n = Number(String(raw || "").replace(/[^0-9.]/g, ""));
  return n > 0 ? `£${Math.round(n).toLocaleString("en-GB")}` : "—";
};

const _titleCase = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : "");

const _dealTs = (d) => {
  const v = d?.updatedAt ?? d?.updated_at ?? d?.createdAt ?? d?.created_at;
  const t = typeof v === "number" ? v : Date.parse(v || "");
  return Number.isFinite(t) ? t : 0;
};

/* Active Deal Room for a property — real property↔deal link only.
 * Completed / fallen-through / archived rooms are skipped (`isActiveDeal`);
 * with several active rooms the most recently updated wins. */
export const activeDealForProperty = (deals, propertyId) => (deals || [])
  .filter((d) => (d.propertyId || d.property_id) === propertyId && isActiveDeal(d))
  .sort((a, b) => _dealTs(b) - _dealTs(a))[0] || null;

const _toCardFromUserProperty = (prop, deal) => ({
  id: prop.id,
  dealId: deal?.id || null,
  address: prop.address || "Untitled property",
  postcode: prop.postcode || "",
  location: [prop.locality, _district(prop.postcode)].filter(Boolean).join(" · ") || "—",
  type: prop.type || "",
  tenure: _titleCase(prop.tenure),
  beds: Number.isFinite(Number(prop.beds)) && prop.beds !== null && prop.beds !== "" ? Number(prop.beds) : null,
  baths: Number.isFinite(Number(prop.baths)) && prop.baths !== null && prop.baths !== "" ? Number(prop.baths) : null,
  price: _askingPrice(prop.price),
  status: STATUS_FROM_PROPERTY[prop.status] || "DRAFT",
  // Same rollup as Deal Rooms "Needs Attention" (canonical classifyDeal: At Risk + Watch on the active deal).
  needsAttention: (() => {
    if (!deal) return false;
    const cls = classifyDeal(deal);
    return cls.atRisk || cls.watch;
  })(),
  parking: prop.parking || null,
  parkingSpaces: prop.parkingSpaces || null,
  thumb: prop.thumb || null,
});

function KpiTile({ icon: Icon, value, label, tone, testid, active, onClick }) {
  /* iter-305 — KPI tiles double as one-tap status filters.
   * The colour vocabulary is locked to the status-badge family
   * (neutral / amber / violet / rose) so the operator reads
   * the same colour from KPI → badge → row tint. Health colours
   * (On Track green · Watch amber · At Risk red) live on a
   * separate scale on each property card. */
  const isInteractive = typeof onClick === "function";
  // Active state surfaces the same colour family as the tile's
  // resting tone — soft ring + filled icon container so the
  // operator never has to guess which filter is on.
  const activeRing =
    tone.includes("slate")   ? "ring-2 ring-slate-400/60 bg-slate-50/40"   :
    tone.includes("amber")   ? "ring-2 ring-amber-300/70 bg-amber-50/50"   :
    tone.includes("violet")  ? "ring-2 ring-violet-300/70 bg-violet-50/50" :
    tone.includes("rose")    ? "ring-2 ring-rose-300/70 bg-rose-50/50"     :
                               "ring-2 ring-slate-300/60 bg-slate-50/40";
  const baseRing = "ring-1 ring-slate-200/70";
  const baseClass =
    "rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-4 py-4 lg:px-4 lg:py-4 xl:px-5 xl:py-5 flex items-center gap-3 lg:gap-3 xl:gap-4 transition";
  const Component = isInteractive ? "button" : "div";
  const interactiveClass = isInteractive
    ? "text-left w-full hover:shadow-[0_4px_14px_-6px_rgba(15,23,42,0.10)] hover:ring-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-400/60 cursor-pointer cf-press"
    : "";
  return (
    <Component
      type={isInteractive ? "button" : undefined}
      onClick={onClick}
      aria-pressed={isInteractive ? !!active : undefined}
      className={`${baseClass} ${interactiveClass} ${active ? activeRing : baseRing}`}
      data-testid={testid}
      data-active={active ? "true" : "false"}
    >
      <div className={`h-10 w-10 xl:h-11 xl:w-11 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="text-[24px] lg:text-[24px] xl:text-[28px] font-bold tabular-nums text-slate-900 leading-none">{value}</div>
        <div className="mt-1.5 text-[12px] xl:text-[12.5px] text-slate-500">{label}</div>
      </div>
    </Component>
  );
}

function FilterDropdown({ label, testid }) {
  /* iter-249 — Visual scaffold for the pilot. The dropdowns mount
   * with the reference vocabulary; live multi-facet filtering is
   * scoped to the search input. The button surface stays calm so
   * the row reads as one operational header. */
  return (
    <button
      type="button"
      className="inline-flex items-center justify-between gap-2 rounded-xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-4 py-2.5 text-[12.5px] font-medium text-slate-700 hover:bg-slate-50 min-w-[150px] cf-press"
      data-testid={testid}
      aria-label={label}
    >
      <span>{label}</span>
      <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
    </button>
  );
}

/* iter-290 — Calm dropdown for the per-card overflow menu. Surfaces
 * the archive / restore action.  iter-alignment · P52 — also
 * surfaces "Edit property", which reuses the existing
 * `openEditProperty` bus event to prefill the AddPropertyDrawer
 * in edit mode.  Closes on outside click + Escape. */
function PropertyRowMenu({ propertyId, archived, onEdit, onArchive, onRestore }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);
  return (
    <div ref={ref} className="absolute top-3 right-3 z-10">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center justify-center h-9 w-9 md:h-8 md:w-8 rounded-lg bg-white/95 shadow-[0_1px_2px_rgba(15,23,42,0.10)] text-slate-600 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
        data-testid={`property-kebab-${propertyId}`}
        aria-label="More property actions"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 w-52 rounded-xl bg-white ring-1 ring-slate-200 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.20)] py-1.5"
          data-testid={`property-menu-${propertyId}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* iter-alignment · P52 — Edit affordance sits above
              archive so operators can jump straight to the edit
              drawer via the existing `openEditProperty` bus event.
              Never rendered for archived cards (edit is a live-
              state action; restore comes first there). */}
          {!archived && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onEdit?.(propertyId); }}
              className="w-full text-left px-3 py-2 inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`property-menu-edit-${propertyId}`}
            >
              <Pencil className="h-3.5 w-3.5 text-slate-500" />
              Edit property
            </button>
          )}
          {archived ? (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onRestore?.(propertyId); }}
              className="w-full text-left px-3 py-2 inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`property-menu-restore-${propertyId}`}
            >
              <RotateCcw className="h-3.5 w-3.5 text-emerald-600" />
              Restore property
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onArchive?.(propertyId); }}
              className="w-full text-left px-3 py-2 inline-flex items-center gap-2 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50"
              data-testid={`property-menu-archive-${propertyId}`}
            >
              <Archive className="h-3.5 w-3.5 text-slate-500" />
              Archive property
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const NAVY = "text-[#16215A]";

function CardAction({ p, archived, actionPending, onRestore }) {
  const cls = "group inline-flex items-center gap-3 py-2.5 -my-2.5 text-[14.5px] font-semibold text-violet-700 hover:text-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300 rounded-md";
  const inner = (label) => (
    <>
      <FileText className="h-[18px] w-[18px] shrink-0" strokeWidth={1.9} />
      <span>{label}</span>
      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" strokeWidth={2.2} />
    </>
  );
  if (archived) {
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); onRestore?.(p.id); }} className={cls} data-testid={`property-restore-btn-${p.id}`}>
        <RotateCcw className="h-[18px] w-[18px]" strokeWidth={1.9} /> Restore property
      </button>
    );
  }
  if (actionPending) {
    return <span className="block h-5 w-44 rounded-md bg-slate-100 animate-pulse" aria-label="Checking deal room" data-testid={`property-cta-loading-${p.id}`} />;
  }
  if (p.dealId) {
    return (
      <Link to={`/deal-rooms/${p.dealId}`} onClick={(e) => e.stopPropagation()} className={cls} data-testid={`property-cta-${p.id}`} data-action="open">
        {inner("Open Deal Room")}
      </Link>
    );
  }
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); openCreateDeal({ propertyId: p.id }); }} className={cls} data-testid={`property-cta-${p.id}`} data-action="create">
      {inner("Create Deal Room")}
    </button>
  );
}

/* Card rows sit on a CSS subgrid so price, details, divider and
 * action line up across every card in a grid row. */
export function PilotPropertyCard({ p, archived = false, onEdit, onArchive, onRestore, actionPending = false }) {
  const tone = STATUS_TONES[p.status] || STATUS_TONES.DRAFT;
  const typeLine = [p.type, p.tenure].filter(Boolean).join(" · ") || "—";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => openProperty({
        id: p.id, address: p.address, postcode: p.postcode,
        image: p.thumb, stage: p.status, price: p.price, value: p.price,
        risk: "low", type: p.type, beds: p.beds, baths: p.baths,
        parking: p.parking, parkingSpaces: p.parkingSpaces,
      })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.currentTarget.click(); }
      }}
      className={`row-span-6 grid grid-rows-subgrid gap-0 mb-5 rounded-2xl bg-white ring-1 ${archived ? "ring-slate-300/80 opacity-90" : "ring-slate-200/70"} shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-18px_rgba(15,23,42,0.18)] overflow-hidden hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_18px_36px_-20px_rgba(15,23,42,0.22)] transition-shadow duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300`}
      data-testid={`property-card-${p.id}`}
      data-archived={archived ? "true" : "false"}
    >
      <div className="relative h-[150px] bg-slate-100 overflow-hidden">
        {p.thumb && <img src={p.thumb} alt={p.address} className="w-full h-full object-cover" loading="lazy" />}
        <span
          className={`absolute top-3 left-3 inline-flex items-center rounded-lg px-2.5 py-1 text-[12.5px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] ${archived ? "bg-slate-900/85 text-white" : tone.pill}`}
          data-testid={`property-status-${p.id}`}
        >
          {archived ? "Archived" : tone.label}
        </span>
        <PropertyRowMenu propertyId={p.id} archived={archived} onEdit={onEdit} onArchive={onArchive} onRestore={onRestore} />
      </div>
      <div className="px-5 pt-5 min-w-0">
        <h3 className={`text-[16px] font-bold leading-snug break-words ${NAVY}`} data-testid={`property-address-${p.id}`}>{p.address}</h3>
        <p className="mt-1 text-[13.5px] text-slate-500 break-words" data-testid={`property-location-${p.id}`}>{p.location || p.postcode || "—"}</p>
      </div>
      <div className="px-5 pt-2.5">
        <p className="text-[22px] font-bold leading-tight text-violet-700 tabular-nums" data-testid={`property-price-${p.id}`}>{p.price}</p>
      </div>
      <div className="px-5 pt-4 flex items-center gap-3 text-[14px] text-slate-600" data-testid={`property-rooms-${p.id}`}>
        <span className="inline-flex items-center gap-2" title="Bedrooms"><BedDouble className="h-[17px] w-[17px] text-slate-500" strokeWidth={1.8} />{p.beds ?? "—"}</span>
        <span className="h-4 w-px bg-slate-200" aria-hidden="true" />
        <span className="inline-flex items-center gap-2" title="Bathrooms"><Bath className="h-[17px] w-[17px] text-slate-500" strokeWidth={1.8} />{p.baths ?? "—"}</span>
      </div>
      <div className="px-5 pt-2.5 min-w-0">
        <p className="text-[13.5px] text-slate-500 leading-snug break-words" data-testid={`property-type-tenure-${p.id}`}>{typeLine}</p>
      </div>
      <div className="mx-5 mt-5 border-t border-slate-100 py-[18px] flex items-center" data-testid={`property-footer-${p.id}`}>
        <CardAction p={p} archived={archived} actionPending={actionPending} onRestore={onRestore} />
      </div>
    </div>
  );
}

export default function PropertiesPage({ embedded = false }) {
  const [q, setQ] = useState("");
  /* iter-305 — Active status filter (toggled from KPI tiles).
   *   null         → no status filter, show every status
   *   "under-offer"  → only "UNDER OFFER"
   *   "sales-agreed" → only "SALES AGREED"
   *   "needs-attention" → only "AT RISK"
   *   "total" never sets a status — clicking Total is a "Show all" reset. */
  const [statusFilter, setStatusFilter] = useState(null);
  const toggleStatusFilter = (key) => setStatusFilter((cur) => (cur === key ? null : key));
  // iter-290 — Active vs Archived view mode. Defaults to "active"
  // so the page always opens to live inventory.
  const [viewMode, setViewMode] = useState("active");
  const {
    properties: storeProperties,
    deals: storeDeals,
    dealsHydrated,
    isPropertyArchived,
    archiveProperty,
    restoreProperty,
    archivedPropertyIds,
  } = useChainStore();

  // iter-290 — Archive / restore handlers, wrapped in calm toasts
  // with a 6-second Undo affordance so accidental archives are
  // trivially reversible.
  const onArchive = useCallback((pid) => {
    const ok = archiveProperty(pid);
    if (!ok) return;
    toast.success("Property archived", {
      description: "Hidden from active views · history preserved",
      action: { label: "Undo", onClick: () => restoreProperty(pid) },
      duration: 6000,
    });
  }, [archiveProperty, restoreProperty]);
  const onRestore = useCallback((pid) => {
    const ok = restoreProperty(pid);
    if (!ok) return;
    toast.success("Property restored", { duration: 4000 });
  }, [restoreProperty]);

  /* iter-alignment · P52 — "Edit property" menu action.  Resolves
   * the property from the store (pilot rows are merged onto
   * `storeProperties` inside the AddPropertyDrawer via a store
   * fallback) and opens the SAME AddPropertyDrawer in edit mode
   * via the pre-existing `openEditProperty` bus event.  The
   * drawer already routes edits through `updateProperty` with
   * optimistic refresh, so no new update path is introduced. */
  const onEdit = useCallback((pid) => {
    const p = (storeProperties || []).find((x) => x.id === pid);
    if (!p) {
      toast.error("Could not open editor", { description: "Property not found." });
      return;
    }
    openEditProperty(p);
  }, [storeProperties]);

  /* iter-alignment · P0 · Pilot Workspace Reseed (2026-08-15)
   * — Postgres is the SSOT for the pilot workspace.  The static
   * `_PILOT_PROPERTIES` overlay has been retired; the seed now
   * mirrors every pilot property into Mongo `properties` so
   * `/api/properties` returns the full 23-row set on its own.
   * User-created rows just append to the API-derived cards. */
  const all = useMemo(() => {
    const userCards = (storeProperties || [])
      .map((prop) => {
        return _toCardFromUserProperty(prop, activeDealForProperty(storeDeals, prop.id));
      });
    return userCards;
  }, [storeProperties, storeDeals]);
  const filtered = useMemo(() => {
    // iter-290 — Two-stage filter:
    //   1) view mode (active vs archived) hides the inverse set.
    //   2) search query trims by address / postcode / type /
    //      progression text — exact pre-iter-290 behaviour.
    // iter-305 — Optional third stage:
    //   3) statusFilter (toggled from the KPI tiles) trims to a
    //      single status family. `null` means "show everything".
    const archiveScoped = all.filter((p) => {
      const isArch = isPropertyArchived(p.id);
      return viewMode === "archived" ? isArch : !isArch;
    });
    const statusScoped = !statusFilter ? archiveScoped : archiveScoped.filter((p) => {
      if (statusFilter === "under-offer")      return p.status === "UNDER OFFER";
      if (statusFilter === "sales-agreed")     return p.status === "SALES AGREED";
      if (statusFilter === "needs-attention")  return p.needsAttention;
      return true;
    });
    if (!q) return statusScoped;
    const t = q.toLowerCase();
    return statusScoped.filter((p) =>
      p.address.toLowerCase().includes(t) ||
      p.postcode.toLowerCase().includes(t) ||
      [p.location, p.type, p.tenure].some((v) => String(v || "").toLowerCase().includes(t)),
    );
  }, [all, q, viewMode, isPropertyArchived, statusFilter]);

  // iter-290 — Archived count exposes the segmented toggle badge
  // independently from `filtered` so the "Archived (N)" pill still
  // reads the true total even when the operator is in "Active" mode.
  const archivedCount = useMemo(
    () => (all.filter((p) => isPropertyArchived(p.id)).length
  ), [all, isPropertyArchived]);

  const kpiCounts = useMemo(() => {
    /* iter-249 — KPI counts derived directly from the calibrated
     * pilot profile so the strip stays truthful as the mix evolves.
     * "Needs attention" counts only the canonical AT RISK status
     * (the operational state surfaces inside cards).
     *
     * iter-290 — Archived properties are excluded from the active
     * KPI tiles (they should never inflate "Total", "Under offer"
     * etc.). The Archived total surfaces inside the Active/Archived
     * segmented toggle, not the KPI strip. */
    const c = { total: 0, underOffer: 0, salesAgreed: 0, needsAttention: 0 };
    all.forEach((p) => {
      if (isPropertyArchived(p.id)) return;
      c.total += 1;
      if (p.status === "UNDER OFFER")  c.underOffer    += 1;
      if (p.status === "SALES AGREED") c.salesAgreed   += 1;
      if (p.needsAttention)            c.needsAttention += 1;
    });
    return c;
  }, [all, isPropertyArchived]);

  const body = (
    <div className={embedded ? "space-y-5 lg:space-y-6" : `${WORKSPACE_SHELL_CLASS} space-y-5 lg:space-y-6`} data-testid="properties-page-container">
      {/* Page header */}
      <PageHeader
        icon={Home}
        title="Properties"
        description="Manage your properties and track transaction progress."
        testid="properties-header"
        actions={(
          <>
            <button
              type="button"
              onClick={() => { /* iter-249 — Import from Rightmove visual surface;
                                wires through the existing Add Property modal so
                                operators can still complete an import via the
                                Add Property dialog in the pilot. */
                openAddProperty({});
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-4 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 cf-press"
              data-testid="properties-import-rightmove-btn"
            >
              <Upload className="h-4 w-4 text-slate-500" /> Import from Rightmove
            </button>
            <button
              type="button"
              onClick={() => openAddProperty({})}
              className="inline-flex items-center gap-2 rounded-xl bg-violet-600 hover:bg-violet-700 px-4 py-2.5 text-[13px] font-semibold text-white shadow-[0_1px_2px_rgba(124,58,237,0.18),0_6px_14px_-8px_rgba(124,58,237,0.40)] cf-press"
              data-testid="properties-add-btn"
            >
              <Plus className="h-4 w-4" /> Add Property
            </button>
          </>
        )}
      />

      {/* KPI strip — iter-305: colours aligned with status badges
          (neutral · amber · violet · rose) and each tile doubles
          as a one-tap status filter. Clicking "Total properties"
          clears any active filter so the operator returns to the
          full set in one click. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="properties-kpi-strip">
        <KpiTile
          icon={Home}
          value={kpiCounts.total}
          label="Total properties"
          tone="bg-slate-100 text-slate-600"
          testid="properties-kpi-total"
          active={statusFilter === null}
          onClick={() => setStatusFilter(null)}
        />
        <KpiTile
          icon={CheckCircle2}
          value={kpiCounts.underOffer}
          label="Under offer"
          tone="bg-amber-50 text-amber-600"
          testid="properties-kpi-under-offer"
          active={statusFilter === "under-offer"}
          onClick={() => toggleStatusFilter("under-offer")}
        />
        <KpiTile
          icon={ShieldAlert}
          value={kpiCounts.salesAgreed}
          label="Sales agreed"
          tone="bg-violet-50 text-violet-600"
          testid="properties-kpi-sales-agreed"
          active={statusFilter === "sales-agreed"}
          onClick={() => toggleStatusFilter("sales-agreed")}
        />
        <KpiTile
          icon={AlertOctagon}
          value={kpiCounts.needsAttention}
          label="Needs attention"
          tone="bg-rose-50 text-rose-600"
          testid="properties-kpi-needs-attention"
          active={statusFilter === "needs-attention"}
          onClick={() => toggleStatusFilter("needs-attention")}
        />
      </div>

      {/* Filter row — Search flexes to fill available width so the
          input never squashes; filter pills + Active/Archived wrap
          onto the next row only when space genuinely runs out. */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="properties-filter-row">
        <div className="flex-1 min-w-[240px]">
          <PremiumInput
            format="search"
            value={q}
            onChange={setQ}
            placeholder="Search address, postcode or type…"
            clearable
            testid="properties-search"
          />
        </div>
        <FilterDropdown label="All statuses" testid="properties-filter-statuses" />
        <FilterDropdown label="All types" testid="properties-filter-types" />
        <FilterDropdown label="All offices" testid="properties-filter-offices" />
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-4 py-2.5 text-[13px] font-semibold text-slate-700 hover:bg-slate-50 cf-press"
          data-testid="properties-filter-more-btn"
        >
          <Filter className="h-4 w-4 text-slate-500" /> Filters
        </button>
        {/* iter-290 — Active / Archived segmented control. Sits at
            the end of the existing filter row so the visual rhythm
            of the page is preserved. */}
        <div
          className="inline-flex items-center rounded-xl bg-slate-100/70 ring-1 ring-slate-200/70 p-1 ml-auto"
          role="tablist"
          aria-label="Property archive view"
          data-testid="properties-archive-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "active"}
            onClick={() => setViewMode("active")}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold transition-colors ${viewMode === "active" ? "bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="properties-archive-toggle-active"
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "archived"}
            onClick={() => setViewMode("archived")}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-[12px] font-semibold transition-colors ${viewMode === "archived" ? "bg-white text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.06)]" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="properties-archive-toggle-archived"
          >
            <Archive className="h-3 w-3" />
            Archived
            {archivedCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold tabular-nums"
                data-testid="properties-archive-toggle-archived-count"
              >
                {archivedCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Property card grid — responsive lane density (iter-alignment
          · P · Properties · Four-Card Review, 2026-03-02):
            base   → 1 col   (mobile)
            md ≥768→ 2 cols  (iPad portrait + landscape)
            lg ≥1024→ 3 cols (laptop — held through xl ≥1280 so
                              14"/15" MacBooks & 1440p Windows
                              screens keep the wider card lanes)
            2xl ≥1536→ 4 cols (wide desktop — capped so each card
                               stays wide enough for the address
                               to wrap to two lines and the price
                               row never clips against the details
                               chips)
          The prior `2xl:grid-cols-5` fifth lane is retired — five
          cards per row squeezed the address into a single-line
          ellipsis and forced the price to fight for space on the
          details row.  Cards stay equal-height inside each row
          via the outer grid; intrinsic sizing on the flex column
          inside the card keeps the CTA anchored to the bottom. */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-5 gap-y-0"
        data-testid="properties-grid"
      >
        {filtered.map((p) => (
          <PilotPropertyCard
            key={p.id}
            p={p}
            archived={viewMode === "archived"}
            onEdit={onEdit}
            onArchive={onArchive}
            onRestore={onRestore}
            actionPending={!dealsHydrated}
          />
        ))}
        {filtered.length === 0 && (
          <div
            className="col-span-full mb-5 text-center py-12 text-[13px] text-slate-500 rounded-2xl ring-1 ring-slate-200/70 bg-white"
            data-testid="properties-empty-state"
          >
            {viewMode === "archived"
              ? "Nothing archived yet."
              : "No properties match that search."}
          </div>
        )}
      </div>

      {/* iter-249 — Bottom calm confirmation state (replaces "Load
          more"). Reads as a deliberate pilot inventory snapshot. */}
      <div
        className="flex flex-col items-center pt-3 text-center"
        data-testid="properties-bottom-state"
      >
        <div className="inline-flex items-center gap-2 text-[12.5px] text-slate-600">
          <Check className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2.4} />
          {viewMode === "archived"
            ? `You've seen all ${filtered.length} archived ${filtered.length === 1 ? "property" : "properties"}`
            : `You've seen all ${kpiCounts.total} properties`}
        </div>
        <div className="mt-1 text-[11.5px] text-slate-400">
          {viewMode === "archived"
            ? "Restore any item to return it to active views."
            : `Add a new property or refine your filters to see more.${archivedPropertyIds && archivedPropertyIds.size > 0 ? ` ${archivedPropertyIds.size} archived hidden.` : ""}`}
        </div>
      </div>
    </div>
  );

  if (embedded) return <div data-testid="properties-main">{body}</div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen" data-testid="properties-main">
        {body}
      </main>
    </div>
  );
}
